// خطة الإشعارات: كل أذانٍ وكل تذكير أذكار في الأيام القادمة، محسوبةً مرة واحدة.
//
// كانت الجدولة تغطّي يومين ولا تتجدّد إلا بفتح التطبيق، فمن لم يفتحه
// ثلاثة أيام انقطع عنه الأذان. الآن تُحسب الخطة لأيام كثيرة وتُسلَّم
// للنظام (أندرويد يحفظها ويسلّحها بنفسه)، والحساب هنا وحده: نسخة واحدة
// من الخوارزمية لا تفترق عن المعروض في قسم المواقيت.

import { getPrayerTimes, prayerLabel } from './prayerTimes';
import { ATHKAR_CATEGORIES } from '../data/athkarData';

const PRAYER_KEYS = ['fajr', 'dhuhr', 'asr', 'maghrib', 'isha'];

export const DEFAULT_ATHKAR_DELAY_MIN = 25;

// مُدد تذكير الأذكار المعروضة؛ من هنا وحده كي لا يفترق المعروض عن المقبول
export const ATHKAR_DELAY_OPTIONS = [10, 15, 20, 25, 30, 40];

/** المدة المحفوظة إن كانت من المعروض، وإلا الافتراضية — قيمةٌ تالفة لا تؤخّر الأذكار ساعات. */
export function normalizeAthkarDelay(value) {
  const n = Number(value);
  return ATHKAR_DELAY_OPTIONS.includes(n) ? n : DEFAULT_ATHKAR_DELAY_MIN;
}

const DAY_MS = 86_400_000;

/*
  ساعة المدينة الآن (٠–٢٣).

  الأيام تُعدّ بخطوات أربعٍ وعشرين ساعة من ظهر المدينة لا من «الآن»: تغيّرُ
  التوقيت الصيفي يزيح الخطوة ساعة، فإن كان «الآن» قرب منتصف الليل تكرّر
  يومٌ أو سقط. ومن الظهر لا تبلغ الإزاحة حدَّ اليوم.
*/
function hourIn(timeZone, at) {
  if (timeZone) {
    try {
      const h = new Intl.DateTimeFormat('en-GB', { timeZone, hour: 'numeric', hourCycle: 'h23' })
        .formatToParts(at)
        .find((p) => p.type === 'hour')?.value;
      if (h != null && !Number.isNaN(Number(h))) return Number(h) % 24;
    } catch {
      // منطقة غير معروفة: ساعة الجهاز
    }
  }
  return at.getHours();
}

/*
  الفجر والعصر يحملان أذكار اليوم معهما: إشعارٌ واحد بدل اثنين متتاليين.
  والعنوان سطرٌ يقول متى يُقال الذكر؛ الذكر نفسه في نصّ الإشعار.
*/
function athkarFor(key) {
  if (key === 'fajr') return { athkar: 'morning', title: 'أذكار الصباح' };
  if (key === 'asr') return { athkar: 'evening', title: 'أذكار المساء' };
  return { athkar: 'after_prayer', title: `بعد صلاة ${prayerLabel(key)}` };
}

/** «(3 مرات)» بعد الذكر — والمرة الواحدة لا تُذكر. */
export function repeatHint(count) {
  const n = Number(count) || 1;
  if (n <= 1) return '';
  if (n === 2) return ' (مرتين)';
  if (n <= 10) return ` (${n} مرات)`;
  return ` (${n} مرة)`;
}

const ITEMS = Object.fromEntries(ATHKAR_CATEGORIES.map((c) => [c.id, c.items]));

/*
  أيّ ذكرٍ يحمله الإشعار.

  كان الإشعار عنواناً و«اضغط لقراءتها»، فلا يقرأ الذكرَ إلا من فتح التطبيق.
  الآن يحمل ذكراً كاملاً، يتبدّل بين الأيام والصلوات فلا يتكرّر نفسه كل مرة.
*/
function dhikrFor(category, dayIndex, prayerIndex) {
  const items = ITEMS[category] ?? [];
  if (items.length === 0) return 'اضغط لقراءة الأذكار';
  const item = items[(dayIndex * PRAYER_KEYS.length + prayerIndex) % items.length];
  return item.text + repeatHint(item.count);
}

/**
 * يبني أحداث الأذان والأذكار القادمة، مرتّبة زمنياً.
 * @returns {Array<{at:number, kind:'adhan'|'athkar', key:string, prayer:string, title:string, body:string, athkar:string|null}>}
 */
export function buildNotificationPlan({
  lat,
  lng,
  methodId,
  madhab,
  timeZone,
  cityName = '',
  days = 2,
  athkar = true,
  athkarDelayMin = DEFAULT_ATHKAR_DELAY_MIN,
  now = new Date(),
}) {
  const nowMs = now.getTime();
  const delayMs = Math.max(0, Number(athkarDelayMin) || 0) * 60_000;
  const adhanBody = cityName ? `حسب توقيت ${cityName}` : 'حان وقت الصلاة';
  const out = [];

  // لا setDate: تقويم الجهاز غير تقويم المدينة، وصيفيّه يكرّر يوماً أو يُسقطه.
  // والتاريخ يُحسب في منطقة المدينة داخل getPrayerTimes
  const noonMs = nowMs + (12 - hourIn(timeZone, now)) * 3_600_000;
  for (let offset = 0; offset < days; offset++) {
    const date = new Date(noonMs + offset * DAY_MS);

    const { times } = getPrayerTimes({ lat, lng, date, methodId, madhab, timeZone });
    for (const key of PRAYER_KEYS) {
      const at = times[key];
      if (!(at instanceof Date)) continue;
      const atMs = at.getTime();
      const prayer = prayerLabel(key);

      if (atMs > nowMs) {
        out.push({
          at: atMs,
          kind: 'adhan',
          key,
          prayer,
          title: `حان الآن وقت صلاة ${prayer}`,
          body: adhanBody,
          athkar: null,
        });
      }

      if (athkar && atMs + delayMs > nowMs) {
        out.push({
          at: atMs + delayMs,
          kind: 'athkar',
          key,
          prayer,
          ...athkarFor(key),
        });
        const ev = out[out.length - 1];
        // اليوم من تاريخ الحدث لا من بداية الخطة: الذكر نفسه في اليوم نفسه أيّاً كان يوم الحساب
        ev.body = dhikrFor(ev.athkar, Math.floor(atMs / DAY_MS), PRAYER_KEYS.indexOf(key));
      }
    }
  }

  // حارسٌ أخير: حدثٌ مكرّر يعني أذاناً يُرفع مرتين
  const seen = new Set();
  const unique = out.filter((e) => {
    const k = `${e.kind}@${e.at}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return unique.sort((a, b) => a.at - b.at);
}
